/**
 * Hook for fetching revision history of a specific appointment record.
 * Uses the endpoint: /api/v1/admin-record/{record_id}/revisions
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import type { RevisionItem, RevisionsResponse } from "@/lib/types/revisions";

/**
 * Fetches revisions for a specific record ID.
 */
async function fetchRecordRevisions(recordId: number): Promise<RevisionItem[]> {
    const { data } = await apiClient.get<RevisionsResponse>(
        apiPath(`/admin-record/${recordId}/revisions`),
        { withCredentials: true }
    );
    return data.data;
}

/**
 * React Query hook for fetching appointment revisions.
 * @param recordId - The appointment record ID
 * @param enabled - Whether the query should run (default: true if recordId is truthy)
 */
export function useRecordRevisions(recordId: number | undefined, enabled = true) {
    return useQuery({
        queryKey: ["record-revisions", recordId],
        queryFn: () => fetchRecordRevisions(recordId!),
        enabled: enabled && !!recordId,
        staleTime: 30_000, // Cache for 30 seconds
        refetchOnWindowFocus: false,
    });
}
